import type {
  FilterCondition,
  FilterGroup,
  JsonSubOperator,
  SpatialFilterContext,
  SpatialSubOperator,
  TableExplorerColumn,
} from '../../core/types/workspaceTypes';
import type { DatabaseMetaInfo } from '../../../../types/electron';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import {
  inferJsonOperatorPlacement,
  categorizeJsonColumn,
  isNativeJsonColumn,
  type JsonColumnInput,
} from './jsonColumnCategorization';
import {
  inferEnumOperatorPlacement,
  type EnumColumnInput,
} from './enumColumnCategorization';
import {
  categorizeSpatialColumn,
  type SpatialColumnInput,
} from './spatialColumnCategorization';
import { isArrayDataType } from './arrayColumnLookup';
import { isNumericColumn } from './columnAlignment';
import { engineSupports } from '../../../../core/engines/engineCapabilities';
import { MIN_PG_MAJOR_VERSION_FOR_DECLARED_JSON } from '../../../../core/engines/pgServerVersion';
import { safeToISOSeconds } from '../../../../utils/dateUtils';
import type { JsonClassification } from '../../../sql-resolver/types/schema';

/**
 * Supported filter operators
 */
export type FilterOperator = FilterCondition['operator'];

/**
 * Data type category for operator selection
 */
export type DataTypeCategory = 'string' | 'number' | 'datetime' | 'boolean' | 'array' | 'unknown';

/**
 * JCS Slice E3 — Filter operators that are semantically valid on a JSON column.
 *
 * v1 surfaces the strict subset of operators that map 1:1 to existing
 * `FilterCondition.operator` shapes and emit valid SQL via `filterWhereBuilder.ts`:
 *   - Equality + null checks across all engines (`=`, `!=`, `IS NULL`, `IS NOT NULL`)
 *   - Substring search for text-backed JSON (`CONTAINS`, `LIKE`) — appropriate
 *     for MSSQL (`nvarchar`-backed CHECK ISJSON), SQLite (text), Oracle CHECK
 *     (CLOB/VARCHAR2), and all `loose-json-*` declarations (text by definition).
 *     Excluded for binary-backed JSON (PG `jsonb`, MySQL `JSON`) where `LIKE`
 *     doesn't operate on the storage form.
 *
 * Ordering operators (`<`/`<=`/`>`/`>=`/`BETWEEN`) and array-cardinality
 * operators are omitted across all JSON classifications — JSON values have no
 * meaningful ordinal comparison at this layer. JSON path-extracted operators
 * (`@>`, `JSON_VALUE(...)`, etc.) are out of scope for E3 because they require
 * extending the `FilterCondition.operator` union plus path/value-pair UX —
 * tracked in the SR-RO C2 lattice for VQE (Slice D2/D3) but not surfaced here.
 *
 * Mirrors `Ralph/JsonColumnSemantics/PRD.md § Slice C2` per-engine lattice
 * intersected with `FilterCondition.operator`.
 */
const JSON_BASE_OPERATORS: readonly FilterOperator[] = [
  '=',
  '!=',
  'IS NULL',
  'IS NOT NULL',
];

const JSON_TEXT_BACKED_EXTRAS: readonly FilterOperator[] = ['CONTAINS', 'LIKE'];

/**
 * Returns true when the JSON column is stored as text (i.e. the engine's JSON
 * is text-backed, or the column is a text column carrying JSON via
 * CHECK ISJSON / a MetaInfo loose-JSON declaration). Binary-JSON engines
 * (PG `jsonb`, MySQL `JSON`) return false — `LIKE`/`CONTAINS` don't operate
 * on the binary storage form.
 */
function isTextBackedJson(
  classification: JsonClassification,
  engineId: DatabaseEngineId | undefined,
): boolean {
  // Text-only classifications regardless of engine.
  if (
    classification.kind === 'check-isjson' ||
    classification.kind === 'loose-json-declared' ||
    classification.kind === 'loose-json-inferred'
  ) {
    return true;
  }
  // Native classifications: binary on pgsql `jsonb` + mysql, text everywhere else.
  if (classification.kind === 'native-jsonb') return false;
  if (classification.kind === 'native-json') {
    // PG `json` (non-binary) accepts text-substring search; MySQL `JSON` is
    // binary-internal; Oracle 21c+ `JSON` is binary-OSON. Conservative
    // default: text only for `pgsql`. Other engines surface base operators.
    return engineId === 'pgsql';
  }
  return false;
}

/**
 * Returns the filter operators valid for a JSON-classified column, given the
 * engine and the classification kind. Always includes `JSON_BASE_OPERATORS`;
 * adds `CONTAINS` + `LIKE` for text-backed JSON storage.
 *
 * Mirrors the SR-RO C2 lattice (`Ralph/JsonColumnSemantics/PRD.md § Slice C2`)
 * intersected with operators expressible via `FilterCondition.operator`.
 */
export function getJsonFilterOperators(
  classification: JsonClassification,
  engineId: DatabaseEngineId | undefined,
): readonly FilterOperator[] {
  const base = [...JSON_BASE_OPERATORS];
  if (isTextBackedJson(classification, engineId)) {
    base.push(...JSON_TEXT_BACKED_EXTRAS);
  }
  return base;
}

/**
 * Operators offered for array-typed columns (Postgres `int[]`, `uuid[]`, …).
 * `array_contains` leads the list — it's the most common need and doubles as
 * the FK-picker entry point for FK array columns.
 */
const ARRAY_OPERATORS: FilterOperator[] = [
  'array_contains',
  'array_not_contains',
  'array_is_empty',
  'array_is_not_empty',
  'array_length_eq',
  'array_length_neq',
  'array_length_gt',
  'array_length_lt',
  'array_length_gte',
  'array_length_lte',
  'IS NULL',
  'IS NOT NULL',
];

/**
 * Get operators available for a given data type
 */
export function getOperatorsForType(category: DataTypeCategory): FilterOperator[] {
  switch (category) {
    case 'string':
      // CONTAINS first so it's the default operator for text columns; equality
      // and the others remain available in the dropdown.
      return ['CONTAINS', '=', '!=', 'LIKE', 'IN', 'NOT IN', 'IS NULL', 'IS NOT NULL'];
    case 'number':
      return ['=', '!=', '<', '<=', '>', '>=', 'BETWEEN', 'IN', 'NOT IN', 'IS NULL', 'IS NOT NULL'];
    case 'datetime':
      return ['=', '!=', '<', '<=', '>', '>=', 'BETWEEN', 'IN', 'NOT IN', 'IS NULL', 'IS NOT NULL'];
    case 'boolean':
      return ['=', 'IS NULL', 'IS NOT NULL'];
    case 'array':
      return [...ARRAY_OPERATORS];
    case 'unknown':
    default:
      // `unknown` covers uuid / uniqueidentifier and columns the caller could
      // not type (a VQE expression, CTE output) — a value list makes sense there.
      return ['=', '!=', 'CONTAINS', '<', '<=', '>', '>=', 'LIKE', 'BETWEEN', 'IN', 'NOT IN', 'IS NULL', 'IS NOT NULL'];
  }
}

/**
 * The list operators ("in list" / "not in list"): their value is a list of
 * values (`FilterValueListInput`), not one.
 */
export function operatorTakesValueList(operator: FilterOperator): boolean {
  return operator === 'IN' || operator === 'NOT IN';
}

/**
 * Get operators available for a column. Identical to `getOperatorsForType`,
 * except FK columns get `lookup` prepended (so it's the default selection),
 * and non-FK columns get `lookup` appended at the bottom — selecting it lets
 * the user declare a loose foreign-key relationship for an unconstrained column.
 *
 * Array columns follow the same rule:
 *   - non-FK array column → `array_contains` is the default (free-text element
 *     membership), `lookup` is appended (loose-FK declaration entry point).
 *   - FK array column     → `lookup` is the default (FK row picker), the array
 *     operators follow. Both filter emitters write `lookup` on an array
 *     column as `array_contains` (`arrayColumnLookup.ts`), so both produce
 *     `<v> = ANY(col)`.
 */
export function getOperatorsForColumn(
  column: TableExplorerColumn | undefined,
  engineId?: DatabaseEngineId,
): FilterOperator[] {
  // JCS Slice E3 — JSON-classified columns dispatch to the engine-correct JSON
  // operator lattice instead of the data-type-only path. Ordering operators
  // (`<`/`>`/`BETWEEN`) and array operators are deliberately omitted; binary
  // JSON (PG `jsonb`, MySQL `JSON`) also omits substring search.
  const jsonClass = column?.jsonClassification;
  if (jsonClass && jsonClass.kind !== 'not-json') {
    const jsonOps = [...getJsonFilterOperators(jsonClass, engineId)];
    return column?.isForeignKey ? ['lookup', ...jsonOps] : [...jsonOps, 'lookup'];
  }

  const baseCategory = column ? categorizeDataType(column.dataType) : 'unknown';
  const baseOperators = getOperatorsForType(baseCategory);
  // A loose-FK "lookup" on a boolean column is nonsensical — never offer it.
  if (baseCategory === 'boolean') {
    return [...baseOperators];
  }
  if (baseCategory === 'array') {
    return column?.isForeignKey
      ? ['lookup', ...baseOperators]
      : [...baseOperators, 'lookup'];
  }
  if (column?.isForeignKey) {
    return ['lookup', ...baseOperators];
  }
  return [...baseOperators, 'lookup'];
}

export type JsonOperatorContext = {
  engine: DatabaseEngineId;
  schema: string;
  table: string;
  metaInfo: DatabaseMetaInfo | null | undefined;
  /**
   * PostgreSQL server major version, when known. Used to gate declare-on-text
   * JSON support — the SQL emit relies on the `IS JSON` predicate (PG 16+),
   * so on older servers the operator is shown in the dropdown but the
   * declare dialog blocks confirmation with a version requirement message.
   * `undefined` means "unknown" (don't gate, let the user proceed and let
   * the engine surface its own error if the version is too old).
   */
  pgMajorVersion?: number | null;
  /**
   * Whether the live SpatiaLite runtime probe passed for this SQLite
   * connection. Only consulted for `engine === 'sqlite'` (whose static
   * capability is `false`); the other engines gate purely on
   * `supportsSpatialFilters`. Undefined/false → SQLite spatial filters hidden.
   */
  sqliteSpatialAvailable?: boolean;
  /**
   * `false` when the host surface has no way to SET a spatial filter's value:
   * the bbox / point comes from a map ("Filter by map view", a map click),
   * which only Table Explorer has. The operator is then neither offered nor
   * defaulted to — a draft without a bbox would apply nothing. Undefined
   * (Table Explorer) keeps the rules in `getOperatorsForColumnWithMetaInfo`.
   */
  offerSpatialOperator?: boolean;
};

/**
 * Whether spatial filter chips (bbox / distance) can be offered for `context`'s
 * engine: the static `supportsSpatialFilters` capability, plus the SQLite
 * runtime probe (SQLite's static capability is `false` — the loadable
 * `mod_spatialite` extension decides at runtime).
 */
export function areSpatialFiltersAvailable(context: JsonOperatorContext): boolean {
  if (engineSupports(context.engine, 'spatial', 'supportsSpatialFilters')) return true;
  return context.engine === 'sqlite' && context.sqliteSpatialAvailable === true;
}

// Minimum PG version for the `IS JSON` predicate behind the declare-on-text
// JSON path. Defined in `core/engines/pgServerVersion.ts` so the main process
// can gate `metainfo_declare`'s json branch on the same number; re-exported
// here because every renderer caller imports it from this module.
export { MIN_PG_MAJOR_VERSION_FOR_DECLARED_JSON };

/**
 * Whether the active engine + version permits declaring a long-text column
 * as JSON. Used by the chip / declare dialog to surface a friendly error
 * before the user invests in a draft they can't apply.
 */
export function isDeclareOnTextSupported(
  jsonContext: JsonOperatorContext | null | undefined,
): boolean {
  if (!jsonContext) return false;
  if (jsonContext.engine !== 'pgsql') return true;
  // PG with unknown version: optimistic — let the user try, the engine will
  // emit a clear error if `IS JSON` isn't recognized.
  if (jsonContext.pgMajorVersion === null || jsonContext.pgMajorVersion === undefined) return true;
  return jsonContext.pgMajorVersion >= MIN_PG_MAJOR_VERSION_FOR_DECLARED_JSON;
}

/**
 * Minimum PostgreSQL major version that supports the jsonpath string methods
 * (`.lower()` et al.) behind the case-insensitive `path_ieq` / `any_ieq`
 * sub-operators. PG ≤ 18 raises a QUERY-TIME jsonpath syntax error on these
 * (there is no pre-parse guard the app can offer), so this gate is the only
 * protection — the two sub-ops must never be emittable below PG 19.
 */
export const MIN_PG_MAJOR_VERSION_FOR_JSONPATH_METHODS = 19;

/**
 * Whether the active engine + version supports the case-insensitive jsonpath
 * sub-operators (`path_ieq` / `any_ieq`). True ONLY for pgsql on a known
 * major version ≥ 19 — these are pgsql-only in v1 (other engines are a
 * documented follow-up). Unlike `isDeclareOnTextSupported`, an unknown
 * version is treated as NOT supported: PG rejects the jsonpath at query time,
 * so an optimistic guess would break PG ≤ 18 users with a hard error.
 */
export function isJsonpathMethodsSupported(
  engine: DatabaseEngineId,
  pgMajorVersion: number | null | undefined,
): boolean {
  if (engine !== 'pgsql') return false;
  if (pgMajorVersion === null || pgMajorVersion === undefined) return false;
  return pgMajorVersion >= MIN_PG_MAJOR_VERSION_FOR_JSONPATH_METHODS;
}

/**
 * Wrap `getOperatorsForColumn` and layer the MetaInfo-derived operators
 * (`json`, then `enum`) at the placement chosen by
 * `inferJsonOperatorPlacement` / `inferEnumOperatorPlacement`.
 *
 * For each layered operator:
 * - `default` / `first` → prepend (becomes the first option; on `default`
 *   the chip auto-selects it for new conditions via `availableOperators[0]`).
 * - `last` → append after the existing operators.
 * - `absent` → leave the list untouched.
 *
 * `enum` is layered *after* `json`, so on the (practically impossible)
 * overlap of a default-json + default-enum column, `enum` wins the index-0
 * auto-select slot. In every realistic case a column is one or the other.
 */
export function getOperatorsForColumnWithMetaInfo(
  column: TableExplorerColumn | undefined,
  context: JsonOperatorContext | null | undefined,
): FilterOperator[] {
  const base = getOperatorsForColumn(column, context?.engine);
  if (!column || !context) return base;
  const jsonInput: JsonColumnInput = {
    engine: context.engine,
    schema: context.schema,
    table: context.table,
    name: column.name,
    dataType: column.dataType,
  };
  let ops = base;
  const jsonPlacement = inferJsonOperatorPlacement(jsonInput, context.metaInfo);
  if (jsonPlacement !== 'absent' && !ops.includes('json')) {
    ops =
      jsonPlacement === 'default' || jsonPlacement === 'first'
        ? ['json', ...ops]
        : [...ops, 'json'];
  }
  // `isNativeEnumType` carries native-enum-ness the type string can't reveal —
  // a PG `CREATE TYPE … AS ENUM` column reports its own type name (`mood`) —
  // so an undeclared native enum still gets the `enum` operator.
  const enumInput: EnumColumnInput = {
    engine: context.engine,
    schema: context.schema,
    table: context.table,
    name: column.name,
    dataType: column.dataType,
    ...(column.isNativeEnumType ? { isNativeEnumType: true } : {}),
  };
  const enumPlacement = inferEnumOperatorPlacement(enumInput, context.metaInfo);
  if (enumPlacement !== 'absent' && !ops.includes('enum')) {
    ops = enumPlacement === 'default' ? ['enum', ...ops] : [...ops, 'enum'];
  }
  // Spatial: offer bbox/distance chips for native-spatial columns when the
  // engine (and, on SQLite, the runtime probe) supports spatial WHERE emit.
  // Appended (not default) — the chips are populated from the map, not typed.
  // T19 — ALSO offer the chip for a DECLARED lat/lng-PAIR column: its numeric
  // range emit (`buildBboxSql`'s `declared-pair` branch) needs no native
  // spatial capability at all, so it's offered on EVERY engine, unlike native-
  // spatial (NOT gated by `areSpatialFiltersAvailable`). Declared WKT/GeoJSON/
  // latlng-text columns are excluded — they hold one opaque value with no
  // numeric sibling range to filter on.
  const spatialInput: SpatialColumnInput = {
    engine: context.engine,
    schema: context.schema,
    table: context.table,
    name: column.name,
    dataType: column.dataType,
  };
  const spatialCategory = categorizeSpatialColumn(spatialInput, context.metaInfo);
  // A geometry value cannot be typed into a list (its `unknown` type category
  // would otherwise offer "in list").
  if (spatialCategory === 'native-spatial' || spatialCategory === 'declared-spatial') {
    ops = ops.filter((op) => !operatorTakesValueList(op));
  }
  if (!ops.includes('spatial') && context.offerSpatialOperator !== false) {
    const nativeOffered = spatialCategory === 'native-spatial' && areSpatialFiltersAvailable(context);
    const declaredPairOffered =
      spatialCategory === 'declared-spatial' &&
      findDeclaredSpatialPairColumn(context, column.name) !== undefined;
    if (nativeOffered || declaredPairOffered) {
      ops = [...ops, 'spatial'];
    }
  }
  return ops;
}

/**
 * Drop the loose-FK `lookup` entry point when the filtered field has no
 * backing table to declare a relationship against. The only such caller
 * today is the VQE HAVING chip's synthetic aggregate fields —
 * `buildAggregateResolvedColumn` marks them with `table: ''` — where
 * offering `lookup` dead-ends (the picker needs a source schema/table, so
 * Apply stays disabled with no explanation). Gates ONLY on that explicit
 * empty-string marker: callers that omit `tableName` entirely (undefined)
 * keep today's full list, and Table Explorer always passes a real table
 * name, so its operator set is untouched.
 */
export function excludeLookupWithoutBackingTable(
  operators: FilterOperator[],
  tableName: string | undefined,
): FilterOperator[] {
  if (tableName !== '') return operators;
  return operators.filter((op) => op !== 'lookup');
}

/**
 * The `lat-lng-pair` declaration for `columnName` in `context`'s table, or
 * `undefined` when there is none (either undeclared, or declared with a
 * different format — WKT/GeoJSON/latlng-text carry no sibling to range-filter
 * on). Shared by the operator-offering check above and `inferSpatialFilterContext`
 * below so both agree on exactly which declared columns are bbox-filterable.
 */
function findDeclaredSpatialPairColumn(
  context: JsonOperatorContext,
  columnName: string,
) {
  return (context.metaInfo?.spatialColumns ?? []).find(
    (d) =>
      d.schema === context.schema &&
      d.table === context.table &&
      d.column === columnName &&
      d.format === 'lat-lng-pair' &&
      !!d.lngColumn,
  );
}

/**
 * Best-known `SpatialFilterContext` for `column`, used to seed the filter
 * chip's sub-operator gating (`canEmitSpatialSubOperator`) BEFORE any bbox/
 * point has been set on the condition — i.e. before "Filter by map view" or a
 * map click has stamped `condition.spatialContext`. For a T19 declared lat/
 * lng-pair column this MUST report `kind: 'declared-pair'` + the sibling
 * `lngColumn`, otherwise the chip's "in map area" sub-op would falsely show
 * disabled on engines whose NATIVE bbox form needs a real geometry column
 * (mysql/oracle/mssql — see `canEmitSpatialSubOperator`). Falls back to the
 * pre-T19 dataType-regex guess (`geography` vs `geometry`, unknown SRID) for
 * everything else.
 */
export function inferSpatialFilterContext(
  column: TableExplorerColumn | undefined,
  context: JsonOperatorContext | null | undefined,
): SpatialFilterContext {
  if (column && context) {
    const pair = findDeclaredSpatialPairColumn(context, column.name);
    if (pair?.lngColumn) {
      return { kind: 'declared-pair', srid: 4326, lngColumn: pair.lngColumn };
    }
  }
  return {
    kind: /^\s*geography/i.test(column?.dataType ?? '') ? 'geography' : 'geometry',
    srid: null,
  };
}

/**
 * Whether `json` should be the auto-selected default operator for a
 * column. Matches the `'default'` placement (native or declared JSON).
 */
export function shouldDefaultToJsonOperator(
  column: TableExplorerColumn | undefined,
  jsonContext: JsonOperatorContext | null | undefined,
): boolean {
  if (!column || !jsonContext) return false;
  const input: JsonColumnInput = {
    engine: jsonContext.engine,
    schema: jsonContext.schema,
    table: jsonContext.table,
    name: column.name,
    dataType: column.dataType,
  };
  return inferJsonOperatorPlacement(input, jsonContext.metaInfo) === 'default';
}

/**
 * Build the initial draft condition fields for a freshly-opened filter on
 * a column. When the column auto-defaults to the `json` operator (native
 * or declared-on-text), this also stamps `jsonSubOperator` and
 * `jsonDeclaredOnText` so the SQL builder picks the correct emit path
 * without waiting for a follow-up edit. Without this, declared-on-text
 * columns would be SQL-emitted as if they were native JSON — the CASE
 * wrap on PG / validity guard on MySQL would be skipped and the query
 * would error against the underlying text column.
 */
/**
 * Whether a newly-added filter on this column should default to the `spatial` operator — true for a
 * geo/spatial field (a native geometry/geography column, or a declared lat/lng pair) that offers the
 * spatial chip. Such a column has no meaningful scalar filter (you can't type a geometry), so a
 * spatial default is what the user expects. Layered AFTER the JSON default (JSON wins the unlikely
 * overlap), mirroring `shouldDefaultToJsonOperator`.
 */
export function shouldDefaultToSpatialOperator(
  column: TableExplorerColumn | undefined,
  context: JsonOperatorContext | null | undefined,
): boolean {
  if (!column || !context) return false;
  // `getOperatorsForColumnWithMetaInfo` only appends 'spatial' when the column is a native-spatial
  // (filters available) or declared-pair column — exactly the geo/spatial-field cases we default on.
  return getOperatorsForColumnWithMetaInfo(column, context).includes('spatial');
}

export function defaultDraftConditionFields(
  column: TableExplorerColumn | undefined,
  jsonContext: JsonOperatorContext | null | undefined,
): {
  operator: FilterOperator;
  value: string;
  jsonSubOperator?: JsonSubOperator;
  jsonDeclaredOnText?: boolean;
  spatialSubOperator?: SpatialSubOperator;
} {
  const operators = getOperatorsForColumnWithMetaInfo(column, jsonContext);
  const isJsonDefault = shouldDefaultToJsonOperator(column, jsonContext);
  const isSpatialDefault = !isJsonDefault && shouldDefaultToSpatialOperator(column, jsonContext);
  const operator: FilterOperator = isJsonDefault
    ? 'json'
    : isSpatialDefault
      ? 'spatial'
      : (operators[0] ?? '=');
  if (isSpatialDefault) {
    // 'intersects_bbox' ("in map area") is the one sub-operator every engine + a declared pair can
    // emit, so it is the safe default; the user can switch to distance/drawn-area in the chip.
    return { operator: 'spatial', value: '', spatialSubOperator: 'intersects_bbox' };
  }
  if (!isJsonDefault || !column || !jsonContext) {
    return { operator, value: '' };
  }
  const input: JsonColumnInput = {
    engine: jsonContext.engine,
    schema: jsonContext.schema,
    table: jsonContext.table,
    name: column.name,
    dataType: column.dataType,
  };
  // `'default'` placement covers both `native-json` and `declared-json`.
  // The validity-guard / CASE-wrap SQL path is only needed for declared-on-text
  // columns; declared *native* JSON columns still emit native SQL.
  const declaredOnText =
    categorizeJsonColumn(input, jsonContext.metaInfo) === 'declared-json' &&
    !isNativeJsonColumn(input);
  return {
    operator,
    value: '',
    jsonSubOperator: 'has_property',
    jsonDeclaredOnText: declaredOnText,
  };
}

/**
 * Get display label for an operator
 */
export function getOperatorLabel(operator: FilterOperator): string {
  switch (operator) {
    case '=': return 'equals';
    case '!=': return 'not equals';
    case '<': return 'less than';
    case '<=': return 'less or equal';
    case '>': return 'greater than';
    case '>=': return 'greater or equal';
    case 'CONTAINS': return 'contains';
    case 'LIKE': return 'like';
    // "between" alone left users unable to tell whether the bounds were
    // included — the reported F02 case (a 101522–101530 range hand-cleaned out
    // of the generated script). Data Compare already relabelled it; carried
    // here in B10 so both surfaces read the same and can't drift again.
    case 'BETWEEN': return 'between (inclusive)';
    case 'IN': return 'in list';
    case 'NOT IN': return 'not in list';
    case 'IS NULL': return 'is null';
    case 'IS NOT NULL': return 'is not null';
    case 'lookup': return 'lookup';
    case 'array_contains': return 'contains';
    case 'array_not_contains': return 'does not contain';
    case 'array_is_empty': return 'is empty';
    case 'array_is_not_empty': return 'is not empty';
    case 'array_length_eq': return 'length =';
    case 'array_length_neq': return 'length ≠';
    case 'array_length_gt': return 'length >';
    case 'array_length_lt': return 'length <';
    case 'array_length_gte': return 'length ≥';
    case 'array_length_lte': return 'length ≤';
    case 'json': return 'json';
    case 'enum': return 'enum';
    case 'spatial': return 'spatial';
    default: return operator;
  }
}

/** Display label for a spatial sub-operator (second dropdown when op = spatial). */
export function getSpatialSubOperatorLabel(sub: SpatialSubOperator): string {
  switch (sub) {
    case 'intersects_bbox': return 'in map area';
    case 'within_distance': return 'within distance';
    case 'intersects_polygon': return 'in drawn area';
    default: return sub;
  }
}

/**
 * Display label for a JSON sub-operator (rendered inside the second
 * dropdown when the top-level operator is `json`).
 *
 * `declaredOnText` controls the labeling of `is_valid` / `is_not_valid`:
 * on a native JSON column those checks are degenerate (the engine
 * already enforces validity), so we relabel them to "is present" /
 * "is null" to keep the dropdown layout stable across declared-on-text
 * vs native columns.
 */
export function getJsonSubOperatorLabel(
  sub: JsonSubOperator,
  declaredOnText: boolean,
): string {
  switch (sub) {
    case 'has_property': return 'has property';
    case 'not_has_property': return 'does not have property';
    case 'path_eq': return 'property =';
    case 'path_ieq': return 'property = (ignore case)';
    case 'path_neq': return 'property !=';
    case 'path_is_null': return 'is null at path';
    case 'path_is_not_null': return 'is not null at path';
    case 'path_contains': return 'property contains';
    case 'path_gt': return 'property >';
    case 'path_gte': return 'property ≥';
    case 'path_lt': return 'property <';
    case 'path_lte': return 'property ≤';
    case 'is_empty': return 'is empty object/array';
    case 'is_not_empty': return 'is not empty object/array';
    case 'is_valid': return declaredOnText ? 'is valid JSON' : 'is present';
    case 'is_not_valid': return declaredOnText ? 'is not valid JSON' : 'is null';
    case 'any_eq': return 'any =';
    case 'any_ieq': return 'any element = (ignore case)';
    case 'any_contains': return 'any contains';
    case 'any_gt': return 'any >';
    case 'any_gte': return 'any ≥';
    case 'any_lt': return 'any <';
    case 'any_lte': return 'any ≤';
    case 'all_eq': return 'all =';
    case 'all_gt': return 'all >';
    case 'all_gte': return 'all ≥';
    case 'all_lt': return 'all <';
    case 'all_lte': return 'all ≤';
    case 'property_lookup': return 'property lookup';
    case 'any_lookup': return 'any lookup';
    case 'all_lookup': return 'all lookup';
    case 'property_enum': return 'property enum';
    case 'any_enum': return 'any enum';
    case 'all_enum': return 'all enum';
  }
}

/**
 * The full ordered list of JSON sub-operators for the dropdown.
 */
export const JSON_SUB_OPERATORS: ReadonlyArray<JsonSubOperator> = [
  'has_property',
  'not_has_property',
  'path_eq',
  'path_ieq',
  'path_neq',
  'path_is_null',
  'path_is_not_null',
  'path_contains',
  'path_gt',
  'path_gte',
  'path_lt',
  'path_lte',
  'property_lookup',
  'property_enum',
  'any_eq',
  'any_ieq',
  'any_contains',
  'any_gt',
  'any_gte',
  'any_lt',
  'any_lte',
  'any_lookup',
  'any_enum',
  'all_eq',
  'all_gt',
  'all_gte',
  'all_lt',
  'all_lte',
  'all_lookup',
  'all_enum',
  'is_empty',
  'is_not_empty',
  'is_valid',
  'is_not_valid',
];

/**
 * Whether a JSON sub-operator needs a JSONPath input. `is_empty`,
 * `is_valid`, `is_not_valid` operate on the top-level value and have
 * no path.
 */
export function jsonSubOperatorRequiresPath(sub: JsonSubOperator): boolean {
  return (
    sub !== 'is_empty' &&
    sub !== 'is_not_empty' &&
    sub !== 'is_valid' &&
    sub !== 'is_not_valid'
  );
}

/**
 * Whether a JSON sub-operator needs a comparand value (`path_eq`,
 * `path_contains`, numeric path comparisons, etc.). The presence/absence
 * sub-ops, the empty check, the validity checks, and the null-at-path
 * checks need no value.
 */
export function jsonSubOperatorRequiresValue(sub: JsonSubOperator): boolean {
  return (
    sub !== 'has_property' &&
    sub !== 'not_has_property' &&
    sub !== 'path_is_null' &&
    sub !== 'path_is_not_null' &&
    sub !== 'is_empty' &&
    sub !== 'is_not_empty' &&
    sub !== 'is_valid' &&
    sub !== 'is_not_valid'
  );
}

/**
 * Numeric path sub-operators take a number value. The UI uses this to
 * pick the value-input category.
 */
export function jsonSubOperatorIsNumeric(sub: JsonSubOperator): boolean {
  return (
    sub === 'path_gt' ||
    sub === 'path_gte' ||
    sub === 'path_lt' ||
    sub === 'path_lte' ||
    sub === 'any_gt' ||
    sub === 'any_gte' ||
    sub === 'any_lt' ||
    sub === 'any_lte' ||
    sub === 'all_gt' ||
    sub === 'all_gte' ||
    sub === 'all_lt' ||
    sub === 'all_lte'
  );
}

/**
 * Aggregate sub-operators (Slice 14) walk a wildcard `[*]` path and
 * either match if any element satisfies (`any_*`) or if every element
 * satisfies (`all_*`). The chip groups them under "Aggregate" in the
 * sub-op dropdown.
 */
export function jsonSubOperatorIsAggregate(sub: JsonSubOperator): boolean {
  return sub.startsWith('any_') || sub.startsWith('all_');
}

/**
 * Lookup-flavored sub-operators (Pass 3 Slice C) source their comparand
 * from the FK row picker rather than free-text input. The chip uses this
 * to render `FkLookupValueButton` in place of the regular value input.
 * Wildcard aggregates (`any_lookup` / `all_lookup`) inherit the wildcard
 * hint defaults from their non-lookup twins (`any_*` / `all_*`).
 */
export function jsonSubOperatorIsLookup(sub: JsonSubOperator): boolean {
  return (
    sub === 'property_lookup' ||
    sub === 'any_lookup' ||
    sub === 'all_lookup'
  );
}

/**
 * Whether a JSON sub-operator targets the column as a whole rather than
 * a JSONPath inside it (`is_valid`, `is_not_valid`, `is_empty`). The
 * Peek popover surfaces these as pills above the path tree.
 */
export function jsonSubOperatorIsDocumentLevel(sub: JsonSubOperator): boolean {
  return (
    sub === 'is_empty' ||
    sub === 'is_not_empty' ||
    sub === 'is_valid' ||
    sub === 'is_not_valid'
  );
}

/**
 * Whether the SQL emit for this sub-operator depends on MSSQL's `OPENJSON`
 * rowset function. `OPENJSON` requires `DATABASE_COMPATIBILITY_LEVEL >= 130`,
 * so callers can use this to gate the chip on lower compat levels even
 * when the path itself doesn't contain `[*]` (notably `is_empty`, which
 * iterates the document via `OPENJSON(col)`).
 *
 * `is_valid` / `is_not_valid` use `ISJSON`, which works at any compat
 * level, so they're explicitly excluded.
 */
export function jsonSubOperatorRequiresOpenJson(sub: JsonSubOperator): boolean {
  if (sub === 'is_empty' || sub === 'is_not_empty') return true;
  if (sub.startsWith('any_') || sub.startsWith('all_')) return true;
  return false;
}

/**
 * Observed JSON path-leaf type, as stored in `JsonStructureCachePathType`,
 * narrowed to a string union the UI can branch on without importing the
 * `electron` typings into utils.
 */
export type JsonPathTypeHint =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'array'
  | 'null'
  | 'mixed';

/**
 * Whether a JSONPath contains a `[*]` wildcard step at any position.
 * Sub-op selection branches on this: scalar leaves *under* an array
 * (e.g. `$.items[*].sku`) need aggregate operators (`any_*` / `all_*`)
 * because path-extract operators (`JSON_VALUE`, `JSON_EXTRACT` text-form)
 * don't handle wildcard paths on most engines.
 */
export function pathHasWildcard(path: string | undefined): boolean {
  if (!path) return false;
  return path.includes('[*]');
}

/**
 * Sub-operators that are *type-relevant* for a given observed path type.
 * Returns a `Set` so the chip can branch its sub-op groups by membership
 * without doing N substring/equality checks per render.
 *
 * `undefined` type ⇒ keep the full set (we don't know the type, so we
 * don't prune). This is what callers get when the path hasn't been
 * resolved in the structure cache yet (e.g. user typed the path freehand).
 *
 * `path` is consulted only to detect mid-path `[*]` wildcards — when
 * present, the aggregate set (`any_*`, `all_*`) replaces the scalar
 * comparators (`path_eq`, …), because path-extract operators don't
 * handle wildcard paths.
 */
export function validSubOpsForType(
  type: JsonPathTypeHint | undefined,
  path?: string,
): Set<JsonSubOperator> {
  if (!type) return new Set(JSON_SUB_OPERATORS);
  // Universally available — make sense regardless of leaf type.
  const universal: JsonSubOperator[] = ['has_property', 'not_has_property'];

  // Scalar leaf *inside* a wildcard array — the user-meaningful filters
  // are aggregate ("any element matches" / "every element matches").
  // `path_*` operators would not lower correctly through JSON_VALUE on
  // a wildcard path.
  if (pathHasWildcard(path) && type !== 'array' && type !== 'object') {
    if (type === 'number') {
      return new Set<JsonSubOperator>([
        ...universal,
        'any_eq',
        'any_gt',
        'any_gte',
        'any_lt',
        'any_lte',
        'any_lookup',
        'any_enum',
        'all_eq',
        'all_gt',
        'all_gte',
        'all_lt',
        'all_lte',
        'all_lookup',
        'all_enum',
      ]);
    }
    if (type === 'boolean') {
      return new Set<JsonSubOperator>([...universal, 'any_eq', 'all_eq']);
    }
    if (type === 'null') {
      return new Set<JsonSubOperator>([
        ...universal,
        'path_is_null',
        'path_is_not_null',
      ]);
    }
    // `string` and `mixed` — full set of element-aggregate comparators.
    return new Set<JsonSubOperator>([
      ...universal,
      'any_eq',
      'any_ieq',
      'any_contains',
      'any_gt',
      'any_gte',
      'any_lt',
      'any_lte',
      'any_lookup',
      'any_enum',
      'all_eq',
      'all_gt',
      'all_gte',
      'all_lt',
      'all_lte',
      'all_lookup',
      'all_enum',
    ]);
  }

  if (type === 'array') {
    return new Set<JsonSubOperator>([
      ...universal,
      'any_eq',
      'any_ieq',
      'any_contains',
      'any_gt',
      'any_gte',
      'any_lt',
      'any_lte',
      'any_lookup',
      'any_enum',
      'all_eq',
      'all_gt',
      'all_gte',
      'all_lt',
      'all_lte',
      'all_lookup',
      'all_enum',
      // `is_empty` / `is_not_empty` are array-shaped too (PG:
      // `jsonb_array_length(col->'p') = 0` / `> 0`).
      'is_empty',
      'is_not_empty',
    ]);
  }
  if (type === 'object') {
    return new Set<JsonSubOperator>([
      ...universal,
      // Comparing the whole subtree as a string is rarely useful but
      // technically supported (`JSON_VALUE` returns NULL → no rows).
      'path_eq',
      'path_neq',
      'path_is_null',
      'path_is_not_null',
      'is_empty',
      'is_not_empty',
    ]);
  }
  if (type === 'number') {
    return new Set<JsonSubOperator>([
      ...universal,
      'path_eq',
      'path_neq',
      'path_is_null',
      'path_is_not_null',
      'path_gt',
      'path_gte',
      'path_lt',
      'path_lte',
      'property_lookup',
      'property_enum',
    ]);
  }
  if (type === 'boolean') {
    return new Set<JsonSubOperator>([
      ...universal,
      'path_eq',
      'path_neq',
      'path_is_null',
      'path_is_not_null',
    ]);
  }
  if (type === 'null') {
    return new Set<JsonSubOperator>([
      ...universal,
      'path_is_null',
      'path_is_not_null',
    ]);
  }
  // `string` and `mixed` — keep the broad set; mixed values may hold any
  // of the primitive shapes so we can't safely prune comparators.
  return new Set<JsonSubOperator>([
    ...universal,
    'path_eq',
    'path_ieq',
    'path_neq',
    'path_is_null',
    'path_is_not_null',
    'path_contains',
    'path_gt',
    'path_gte',
    'path_lt',
    'path_lte',
    'property_lookup',
    'property_enum',
  ]);
}

/**
 * Pick the default sub-op the chip should auto-select when the user has
 * just picked a path with the observed `type`. Tuned for the most likely
 * follow-up action — string contains, number/boolean equality, object
 * presence check, array `any =`.
 *
 * When `path` carries a mid-position `[*]` wildcard (e.g.
 * `$.items[*].sku`), the default flips to the aggregate variant —
 * `any_contains` / `any_eq` / `any_is_null` — because path-extract
 * operators won't lower correctly through a wildcard step.
 */
export function defaultSubOpForType(
  type: JsonPathTypeHint | undefined,
  path?: string,
): JsonSubOperator {
  const wildcard = pathHasWildcard(path);
  if (wildcard) {
    switch (type) {
      case 'array':
        return 'any_eq';
      case 'object':
        // Picking the `[*]` row itself (object element of an array).
        // `has_property` answers "every element shares a property" with
        // `JSON_EXISTS`, which is the most common starting filter.
        return 'has_property';
      case 'number':
      case 'boolean':
        return 'any_eq';
      case 'null':
        return 'path_is_null';
      case 'string':
        return 'any_contains';
      default:
        return 'any_eq';
    }
  }
  switch (type) {
    case 'array':
      return 'any_eq';
    case 'object':
      return 'has_property';
    case 'number':
      return 'path_eq';
    case 'boolean':
      return 'path_eq';
    case 'null':
      return 'path_is_null';
    case 'string':
      return 'path_contains';
    default:
      return 'path_eq';
  }
}

/**
 * Default sub-op to stamp when the user explicitly switches the chip
 * into "whole column" mode (via the Peek popover's footer link).
 *
 * UX decision: whole-column mode always defaults to `is_not_empty`
 * regardless of storage flavor (native JSON or declared-on-text). It
 * answers "did the user actually populate this JSON?" — the most
 * commonly useful document-level predicate — and works equivalently
 * across all 5 engines. The `is_valid` distinction for declared-on-text
 * is now opt-in via the explicit pick in the popover rather than the
 * implicit default, so users don't land on a validity check they
 * didn't ask for.
 */
export function defaultDocumentSubOpForEngine(
  declaredOnText: boolean,
): 'is_not_empty' {
  void declaredOnText;
  return 'is_not_empty';
}

/**
 * Adjust a JSONPath's *terminal* `[*]` to match the requirements of a
 * sub-op. Aggregate sub-ops (`any_*`, `all_*`) need the path to end in
 * a wildcard step — append `[*]` when missing. Non-aggregate sub-ops
 * strip a trailing `[*]` so the path lands on the container itself.
 *
 * Mid-path wildcards (`$.items[*].sku`) are never touched — that's the
 * user's explicit shape and only the terminal segment is op-driven.
 */
export function adjustPathForSubOp(
  path: string,
  sub: JsonSubOperator,
): string {
  if (!path) return path;
  const aggregate = jsonSubOperatorIsAggregate(sub);
  if (aggregate) {
    // Aggregate sub-ops need *some* `[*]` step in the path so the
    // engine knows what to iterate. If a mid-path wildcard is already
    // present (e.g. `$.items[*].sku`) the path is already iterable —
    // don't append another. Otherwise add a terminal `[*]`.
    if (pathHasWildcard(path)) return path;
    return `${path}[*]`;
  }
  // Lookup variants (`property_lookup`, `any_lookup`, `all_lookup`)
  // already pass through `jsonSubOperatorIsAggregate` for the any/all
  // forms. `property_lookup` targets a scalar — strip a terminal `[*]`
  // so the path lands on the property the lookup compares against.
  // Mid-path `[*]` (`$.items[*].sku`) is the user's intent and stays.
  if (path.endsWith('[*]')) {
    return path.substring(0, path.length - 3);
  }
  return path;
}

/**
 * Check if operator requires a value. The `json` operator's value
 * requirement depends on its sub-operator — callers that need precise
 * JSON-aware value gating should use `jsonSubOperatorRequiresValue`
 * directly. We default to `false` for `json` so the chip doesn't render
 * a stray free-text value input next to the sub-op dropdown.
 */
export function operatorRequiresValue(operator: FilterOperator): boolean {
  return (
    operator !== 'IS NULL' &&
    operator !== 'IS NOT NULL' &&
    operator !== 'array_is_empty' &&
    operator !== 'array_is_not_empty' &&
    operator !== 'json' &&
    // `spatial` renders its own field cluster (SpatialFilterFields), not the
    // standard value input — bbox/point/distance come from the map.
    operator !== 'spatial'
  );
}

/**
 * Whether `value` is a filled-in operand: not empty text / null / undefined,
 * and a list holds at least one value (an empty "in list" applies nothing).
 */
export function hasFilterValue(value: FilterCondition['value'] | undefined): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return value !== '' && value !== null && value !== undefined;
}

/**
 * The value a condition keeps when its operator changes to `operator`: a list
 * operator gets the current value as a one-value list (and keeps a list — an
 * `enum` multi-pick — as is); a single-value operator gets a list's first
 * value; `enum` keeps whatever it had (its picker takes both). Undefined for
 * an operator that takes no value.
 */
export function valueForOperator(
  value: FilterCondition['value'] | undefined,
  operator: FilterOperator,
): FilterCondition['value'] | undefined {
  if (!operatorRequiresValue(operator)) return undefined;
  if (operator === 'enum') return value;
  if (operatorTakesValueList(operator)) {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' || typeof value === 'number') return value === '' ? [] : [value];
    if (value instanceof Date) return [value.toISOString()];
    return [];
  }
  return Array.isArray(value) ? value[0] : value;
}

/**
 * Check if operator requires a second value (BETWEEN)
 */
export function operatorRequiresSecondValue(operator: FilterOperator): boolean {
  return operator === 'BETWEEN';
}

/**
 * Array-length operators take an integer N (cardinality(col) <op> N).
 * UI uses this to render a number input instead of free-text.
 */
export function operatorIsArrayLength(operator: FilterOperator): boolean {
  return (
    operator === 'array_length_eq' ||
    operator === 'array_length_neq' ||
    operator === 'array_length_gt' ||
    operator === 'array_length_lt' ||
    operator === 'array_length_gte' ||
    operator === 'array_length_lte'
  );
}

/**
 * Element-membership operators on array columns. The value is a single
 * element of the array's element type (e.g. `int` for `int[]`).
 */
export function operatorIsArrayElement(operator: FilterOperator): boolean {
  return operator === 'array_contains' || operator === 'array_not_contains';
}

/**
 * Strip a trailing `[]` from a SQL data type string. Used to derive the
 * element type for value-input categorization on array columns.
 * Also handles whitespace + multi-dim arrays (`int[][]` → `int`).
 */
export function arrayElementDataType(dataType: string): string {
  return dataType.replace(/(\s*\[\s*\])+$/u, '').trim();
}

/**
 * Categorize a SQL data type
 */
export function categorizeDataType(dataType: string): DataTypeCategory {
  const lower = dataType.toLowerCase();

  // Array types come first — Postgres `int[]`, `uuid[]`, `text[]`, etc. The
  // category drives a different operator set; without this the array gets
  // miscategorised by its element type (e.g. `integer[]` would otherwise
  // include `int` and return `'number'`, surfacing nonsense `<col> > 5`-style
  // operators in the dropdown). The emitters key the `lookup` → membership
  // rewrite on the same test (`arrayColumnLookup.ts`).
  if (isArrayDataType(lower)) {
    return 'array';
  }

  // Boolean types
  if (lower === 'bit' || lower === 'boolean' || lower === 'bool') {
    return 'boolean';
  }

  // Numeric types — the results grids' right-align predicate holds the one
  // numeric-type list: whole words of the argument-stripped type, so Oracle
  // `NUMBER(10,2)` / `BINARY_DOUBLE`, PG `double precision`, MySQL / SQLite
  // `DOUBLE` and Kusto's `long` are numbers, while `interval`, `point` and
  // `int4range` (which merely contain "int") are not. Oracle's deprecated
  // `LONG` char type also lands here; it cannot appear in a WHERE predicate
  // anyway, so its filter-operator category is moot.
  if (isNumericColumn({ dataType })) {
    return 'number';
  }

  // Type arguments carry no category signal but can carry a false one: MySQL's
  // COLUMN_TYPE spells ENUM / SET members inline (`enum('update','done')`
  // contains "date").
  const base = lower.replace(/\(.*$/su, '');

  // DateTime types
  if (
    base.includes('date') ||
    base.includes('time') ||
    base.includes('timestamp')
  ) {
    return 'datetime';
  }

  // String types
  if (
    base.includes('char') ||
    base.includes('text') ||
    base.includes('varchar') ||
    base.includes('nvarchar') ||
    base.includes('nchar') ||
    base.includes('ntext')
  ) {
    return 'string';
  }

  return 'unknown';
}

/** Values of a list a chip's summary spells out before "+N more". */
const LISTED_VALUES = 5;

/**
 * Format a filter value for display
 */
export function formatFilterValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'NULL';
  }
  if (Array.isArray(value)) {
    // An "in list" value: the first few, then how many more.
    const shown = value.slice(0, LISTED_VALUES).map(formatFilterValue).join(', ');
    return value.length > LISTED_VALUES ? `${shown} +${value.length - LISTED_VALUES} more` : shown;
  }
  if (value instanceof Date) {
    return safeToISOSeconds(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
}

/**
 * Generate a unique ID for filter groups
 */
export function generateFilterId(): string {
  return `filter-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Filters to scope a chip's JSON peek scan to: every APPLIED filter (locked
 * breadcrumb constraints first, then committed chips) minus the chip's own
 * group when it is a committed chip being re-edited — scoping a payload peek
 * to the payload chip's own stale condition would circularly narrow the
 * suggestions. Order must stay [...locked, ...committed] — it feeds
 * filterSignature() and must serialize identically to the grid's
 * effectiveFilters. (Docs/JsonDiscriminatedStructure.md, Path A.)
 */
export function scanFiltersForChip(
  locked: ReadonlyArray<FilterGroup>,
  committed: ReadonlyArray<FilterGroup>,
  excludeIndex: number | null,
): FilterGroup[] {
  const rest =
    excludeIndex === null ? committed : committed.filter((_, i) => i !== excludeIndex);
  return [...locked, ...rest];
}
